import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { MailApi } from '@/api/contract';
import { ApiError } from '@/api/http';
import { useApi } from '@/api/provider';
import { STALE_TIMES } from '@/api/queryClient';
import { queryKeys, type UnifiedListFilters } from '@/api/queryKeys';
import type { MessageSortOrder, MessageSummary, Paginated, SearchPage } from '@/api/types';
import {
  emptyLanes,
  laneFailures,
  lanesHaveMore,
  lanesToRefill,
  mergeLanes,
  type UnifiedLane,
  type UnifiedLaneProblem,
  type UnifiedLanes,
} from './unifiedList';
import { withFilterTerms } from './searchScope';

/**
 * The merged mail list (ADR 0056): one `useInfiniteQuery` whose `pageParam`
 * **is** the interleaving cursor.
 *
 * The lanes are fetched from the ordinary per-account endpoint — no new route,
 * no server-side merge — and interleaved by `unifiedList.ts`, which is pure.
 * This file is the impure half: it knows about the API client and TanStack
 * Query and nothing about ordering.
 */

/** One mailbox's Inbox, the lane's source. */
export interface UnifiedInbox {
  accountId: string;
  folderId: string;
}

/** What the merged list asks its lanes for, besides paging. */
export interface UnifiedFilters {
  search?: string;
  /**
   * With `search`: each lane searches every folder of its mailbox (the
   * cross-folder search endpoint) instead of listing its Inbox.
   */
  allFolders?: boolean;
  includeTrashJunk?: boolean;
  unreadOnly?: boolean;
  flaggedOnly?: boolean;
}

/**
 * A merged page.
 *
 * It is a `Paginated<MessageSummary>` so the cache patches
 * (`cache/messagePatches.ts`) apply to it unchanged — they rebuild a page as
 * `{...page, items}` and leave `lanes` alone, which is correct: archiving a row
 * does not move any lane's cursor.
 */
export interface UnifiedPage extends Paginated<MessageSummary> {
  /** The cursor for the page after this one. */
  lanes: UnifiedLanes;
  /** The mailboxes that could not be read, cumulative. */
  unavailable: UnifiedLaneProblem[];
  /**
   * Folders a cross-folder lane could not search on the last round (timed out
   * or failed), for the "results may be incomplete" banner.
   */
  foldersMissed?: SearchFolderGap[];
}

/** One folder of one mailbox that a cross-folder search could not read. */
export interface SearchFolderGap {
  accountId: string;
  folderId: string;
  reason: 'timeout' | 'failed';
}

/** Rows fetched per lane per round. See ADR 0056 for why this is not divided by N. */
export const UNIFIED_LANE_PAGE_SIZE = 50;

/**
 * How many fetch/merge rounds one page may take before giving up.
 *
 * A round that emits nothing is normal exactly once — the first, before any
 * lane has a buffer — and after a lane is drained. The bound is what stops a
 * pathological server (a page of 0 rows with a next cursor, forever) from
 * spinning the queryFn.
 */
const MAX_ROUNDS = 6;

/**
 * Fetches and merges one page, refilling only the lanes that ran dry.
 *
 * A lane whose request rejects is marked failed and **excluded** from then on:
 * it no longer holds the watermark back and it is not asked again. That is what
 * keeps one unreachable mailbox from emptying the list (ADR 0056 §3). Retrying
 * is the banner's job, and it drops the whole query rather than poking one lane,
 * so the merge always starts from a consistent set of cursors.
 */
export async function fetchUnifiedPage(options: {
  api: MailApi;
  inboxes: readonly UnifiedInbox[];
  lanes: UnifiedLanes;
  filters: UnifiedFilters;
  sort: MessageSortOrder;
  limit?: number;
  signal?: AbortSignal;
}): Promise<UnifiedPage> {
  const { api, inboxes, filters, sort, signal } = options;
  const limit = options.limit ?? UNIFIED_LANE_PAGE_SIZE;
  const folderOf = new Map(inboxes.map((inbox) => [inbox.accountId, inbox.folderId]));

  let lanes = options.lanes;
  let items: MessageSummary[] = [];
  let partial = false;
  const foldersMissed: SearchFolderGap[] = [];
  const sortsSeen: MessageSortOrder[][] = [];

  for (let round = 0; round < MAX_ROUNDS && items.length === 0; round += 1) {
    const refill = lanesToRefill(lanes).filter((accountId) => folderOf.has(accountId));
    if (refill.length > 0) {
      const answers = await Promise.allSettled(
        refill.map((accountId) =>
          filters.allFolders && filters.search
            ? /*
               * Search across every folder of the lane's mailbox. The server
               * merges its folders into one page in `sort` order behind its own
               * interleaving cursor, so to this merge the lane is exactly what
               * an Inbox listing was: one ordered stream per account.
               */
              api.messages.search(
                accountId,
                {
                  query: withFilterTerms(
                    filters.search,
                    filters.unreadOnly ? 'unread' : filters.flaggedOnly ? 'flagged' : 'all',
                  ),
                  scope: 'all',
                  ...(filters.includeTrashJunk ? { includeTrashJunk: true } : {}),
                  sort,
                  limit,
                  ...(lanes[accountId]?.cursor ? { cursor: lanes[accountId]!.cursor } : {}),
                },
                signal,
              )
            : api.messages.list(
                {
                  accountId,
                  folderId: folderOf.get(accountId)!,
                  sort,
                  limit,
                  ...(lanes[accountId]?.cursor ? { cursor: lanes[accountId]!.cursor } : {}),
                  ...(filters.search ? { search: filters.search } : {}),
                  ...(filters.unreadOnly ? { unreadOnly: true } : {}),
                  ...(filters.flaggedOnly ? { flaggedOnly: true } : {}),
                },
                signal,
              ),
        ),
      );

      const next: Record<string, UnifiedLane> = { ...lanes };
      refill.forEach((accountId, index) => {
        const answer = answers[index]!;
        const lane = lanes[accountId]!;
        if (answer.status === 'rejected') {
          next[accountId] = { ...lane, failure: describeLaneFailure(answer.reason) };
          return;
        }
        const page = answer.value;
        if (page.partial) partial = true;
        for (const folderId of (page as SearchPage).foldersTimedOut ?? []) {
          foldersMissed.push({ accountId, folderId, reason: 'timeout' });
        }
        for (const folderId of (page as SearchPage).foldersFailed ?? []) {
          foldersMissed.push({ accountId, folderId, reason: 'failed' });
        }
        if (page.sortsAvailable) sortsSeen.push(page.sortsAvailable);
        // The spread drops the consumed cursor: a lane that has just been
        // refilled carries only the cursor for its NEXT page, if there is one.
        const { cursor: _consumed, ...rest } = lane;
        next[accountId] = {
          ...rest,
          // Dedupe inside the lane: cursor paging repeats a row when mail
          // arrives between two page fetches.
          buffer: dedupe([...lane.buffer, ...page.items]),
          ...(page.nextCursor ? { cursor: page.nextCursor } : {}),
          exhausted: page.nextCursor === undefined,
        };
      });
      lanes = next;
    } else if (!lanesHaveMore(lanes)) {
      break;
    }

    const merged = mergeLanes(lanes, sort);
    items = merged.items;
    lanes = merged.rest;

    // Nothing emitted and nothing left to ask for: the lanes are as far along
    // as they can get, so another round would repeat this one.
    if (items.length === 0 && lanesToRefill(lanes).length === 0) break;
  }

  const unavailable = laneFailures(lanes);
  return {
    items,
    lanes,
    unavailable,
    ...(foldersMissed.length > 0 ? { foldersMissed } : {}),
    ...(partial || unavailable.length > 0 ? { partial: true } : {}),
    sort,
    ...(sortsSeen.length > 0 ? { sortsAvailable: intersect(sortsSeen) } : {}),
  };
}

function dedupe(messages: readonly MessageSummary[]): MessageSummary[] {
  const seen = new Set<string>();
  const out: MessageSummary[] = [];
  for (const message of messages) {
    if (seen.has(message.id)) continue;
    seen.add(message.id);
    out.push(message);
  }
  return out;
}

function intersect(sets: readonly MessageSortOrder[][]): MessageSortOrder[] {
  const [first, ...rest] = sets;
  return (first ?? []).filter((order) => rest.every((set) => set.includes(order)));
}

/**
 * What to say about a mailbox that would not load.
 *
 * `reauth` is the one failure a "Try again" cannot fix: the backend has stopped
 * retrying that mailbox and will not start until it has a password (#254's
 * `409 credential_invalid`), so the banner offers the accounts page instead of
 * a button that will fail the same way.
 */
export function describeLaneFailure(error: unknown): { message: string; reauth: boolean } {
  if (error instanceof ApiError) {
    if (error.status === 409 && error.details?.['account_status'] === 'reauth-required') {
      return { message: 'needs its password again', reauth: true };
    }
    if (error.isSessionExpired) return { message: 'needs you to sign in again', reauth: true };
    if (error.isOffline) return { message: 'is unreachable while you are offline', reauth: false };
    if (error.isServiceUnavailable) return { message: 'is temporarily unavailable', reauth: false };
    if (error.status === 504) return { message: 'did not answer in time', reauth: false };
  }
  return { message: 'could not be loaded', reauth: false };
}

export interface UseUnifiedMessagesOptions {
  inboxes: readonly UnifiedInbox[];
  filters: UnifiedFilters;
  sort: MessageSortOrder;
  enabled: boolean;
}

/** The merged list as a query. Mirrors the shape `MailView` uses for one folder. */
export function useUnifiedMessages(options: UseUnifiedMessagesOptions) {
  const api = useApi();
  const { inboxes, filters, sort, enabled } = options;

  const accountIds = useMemo(
    () => inboxes.map((inbox) => inbox.accountId).sort(),
    [inboxes],
  );

  const key: UnifiedListFilters = useMemo(
    () => ({
      accounts: accountIds.join(','),
      sort,
      ...(filters.search ? { search: filters.search } : {}),
      ...(filters.search && filters.allFolders ? { allFolders: true } : {}),
      ...(filters.search && filters.allFolders && filters.includeTrashJunk
        ? { includeTrashJunk: true }
        : {}),
      ...(filters.unreadOnly ? { unreadOnly: true } : {}),
      ...(filters.flaggedOnly ? { flaggedOnly: true } : {}),
    }),
    [
      accountIds,
      filters.allFolders,
      filters.flaggedOnly,
      filters.includeTrashJunk,
      filters.search,
      filters.unreadOnly,
      sort,
    ],
  );

  const queryKey = useMemo(() => queryKeys.unifiedMessages(key), [key]);

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) =>
      fetchUnifiedPage({
        api,
        inboxes,
        lanes: pageParam ?? emptyLanes(accountIds),
        filters,
        sort,
        signal,
      }),
    initialPageParam: undefined as UnifiedLanes | undefined,
    getNextPageParam: (lastPage: UnifiedPage) =>
      lanesHaveMore(lastPage.lanes) ? lastPage.lanes : undefined,
    enabled: enabled && inboxes.length > 0,
    staleTime: STALE_TIMES.list,
  });

  return { query, queryKey };
}
